import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { getEventRegistrationById } from "../../Utils/apiServices";
import Button from "../../Components/Button";
import Card from "../../Components/Card";
import { Typography } from "../../Components/Typography";

const EventRegistrationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRegistrationDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getEventRegistrationById(id);
      if (response.success && response.data?.data) {
        setRegistration(
          Array.isArray(response.data.data) ? response.data.data[0] : response.data.data
        );
      } else {
        setError("Registration not found");
      }
    } catch (err) {
      console.error("Error fetching registration details:", err);
      setError(err.error || "Failed to load registration details");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchRegistrationDetails();
  }, [fetchRegistrationDetails]);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const formatValue = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return value;
  };

  const formatArray = (arr) => {
    if (!Array.isArray(arr) || arr.length === 0) return "-";
    return arr.join(", ");
  };

  const formatStatus = (status) => {
    const statusMap = {
      registered: "Applied",
      active: "Active",
      rejected: "Rejected",
      approved: "Active",
    };
    return statusMap[status] || status || "Applied";
  };

  const getStatusColor = (status) => {
    const formattedStatus = formatStatus(status);
    if (formattedStatus === "Active") {
      return "bg-green-100 text-green-800";
    } else if (formattedStatus === "Rejected") {
      return "bg-red-100 text-red-800";
    }
    return "bg-yellow-100 text-yellow-800";
  };

  const getDisplayName = (registration) => {
    if (registration?.email) {
      const emailPrefix = registration.email.split("@")[0];
      return emailPrefix
        .split(/[._-]/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    }
    return "Unknown";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Typography as="p" className="text-tertiary-600">
          Loading registration details...
        </Typography>
      </div>
    );
  }

  if (error || !registration) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4">
        <Typography as="p" className="text-red-600">
          {error || "Registration not found"}
        </Typography>
        <Button variant="secondary" onClick={() => navigate("/volunteers")}>
          Back to Volunteers
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-auto gap-6 bg-primary px-8 py-8">
      {/* Header */}
      <div className="flex flex-col gap-3 w-full sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="secondary"
            size="small"
            onClick={() => navigate("/volunteers")}
            startIcon={<Icon icon="lucide:arrow-left" className="h-4 w-4" />}
          >
            Back
          </Button>
          <Typography as="h1" className="text-2xl font-bold text-primary-900">
            Volunteer Registration Details
          </Typography>
        </div>
      </div>

      {/* Profile Header */}
      <Card className="w-full shadow-sm border border-slate-200">
        <div className="p-8">
          <div className="flex items-start gap-8">
            <div className="h-32 w-32 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center flex-shrink-0 overflow-hidden border-4 border-white shadow-lg">
              <Icon icon="lucide:user" className="h-16 w-16 text-slate-400" />
            </div>
            <div className="flex-1 pt-2">
              <div className="flex items-center gap-3 mb-3">
                <Typography as="h2" className="text-2xl font-bold text-primary-900">
                  {getDisplayName(registration)}
                </Typography>
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                    registration.status
                  )}`}
                >
                  {formatStatus(registration.status)}
                </span>
              </div>
              <div className="flex items-center gap-2 mb-2">
                <Icon icon="lucide:mail" className="h-4 w-4 text-tertiary-600" />
                <Typography as="p" className="text-sm text-tertiary-600">
                  {registration.email}
                </Typography>
              </div>
              {registration.event && (
                <div className="flex items-center gap-2">
                  <Icon icon="lucide:calendar" className="h-4 w-4 text-tertiary-600" />
                  <Typography as="p" className="text-sm font-medium text-primary-700">
                    {registration.event.title}
                  </Typography>
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Personal & Professional Information */}
        <Card className="w-full shadow-sm border border-slate-200">
          <div className="p-6">
            <div className="flex items-center gap-2 mb-6 pb-4 border-b border-slate-200">
              <Icon icon="lucide:user" className="h-5 w-5 text-purple-600" />
              <Typography as="h3" className="text-lg font-semibold text-primary-900">
                Personal & Professional Information
              </Typography>
            </div>
            <div className="space-y-5">
              <div className="flex flex-col gap-1.5 pb-4 border-b border-slate-100">
                <Typography as="label" className="text-xs font-semibold text-tertiary-600 uppercase tracking-wider">
                  Email
                </Typography>
                <Typography as="p" className="text-sm font-medium text-primary-900">
                  {formatValue(registration.email)}
                </Typography>
              </div>
              <div className="flex flex-col gap-1.5 pb-4 border-b border-slate-100">
                <Typography as="label" className="text-xs font-semibold text-tertiary-600 uppercase tracking-wider">
                  Current Occupation
                </Typography>
                <Typography as="p" className="text-sm font-medium text-primary-900">
                  {formatValue(registration.current_occupation)}
                </Typography>
              </div>
              <div className="flex flex-col gap-1.5 pb-4 border-b border-slate-100">
                <Typography as="label" className="text-xs font-semibold text-tertiary-600 uppercase tracking-wider">
                  Organization/Employer
                </Typography>
                <Typography as="p" className="text-sm font-medium text-primary-900">
                  {formatValue(registration.organization_employer)}
                </Typography>
              </div>
              <div className="flex flex-col gap-1.5 pb-4 border-b border-slate-100">
                <Typography as="label" className="text-xs font-semibold text-tertiary-600 uppercase tracking-wider">
                  Job Title/Major
                </Typography>
                <Typography as="p" className="text-sm font-medium text-primary-900">
                  {formatValue(registration.job_title_or_major)}
                </Typography>
              </div>
              <div className="flex flex-col gap-1.5 pb-4 border-b border-slate-100">
                <Typography as="label" className="text-xs font-semibold text-tertiary-600 uppercase tracking-wider">
                  Years of Experience
                </Typography>
                <Typography as="p" className="text-sm font-medium text-primary-900">
                  {formatValue(registration.years_of_experience)}
                </Typography>
              </div>
              <div className="flex flex-col gap-1.5 pb-4 border-b border-slate-100 last:border-0 last:pb-0">
                <Typography as="label" className="text-xs font-semibold text-tertiary-600 uppercase tracking-wider">
                  LinkedIn Profile
                </Typography>
                <Typography as="p" className="text-sm font-medium text-primary-900">
                  {registration.linkedin_profile && registration.linkedin_profile !== "-" ? (
                    <a
                      href={registration.linkedin_profile}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-blue hover:underline flex items-center gap-1"
                    >
                      {registration.linkedin_profile}
                      <Icon icon="lucide:external-link" className="h-3 w-3" />
                    </a>
                  ) : (
                    <span className="text-tertiary-500 italic">-</span>
                  )}
                </Typography>
              </div>
            </div>
          </div>
        </Card>

        {/* Expertise & Roles */}
        <Card className="w-full shadow-sm border border-slate-200">
          <div className="p-6">
            <div className="flex items-center gap-2 mb-6 pb-4 border-b border-slate-200">
              <Icon icon="lucide:briefcase" className="h-5 w-5 text-purple-600" />
              <Typography as="h3" className="text-lg font-semibold text-primary-900">
                Expertise & Roles
              </Typography>
            </div>
            <div className="space-y-5">
              <div className="flex flex-col gap-1.5 pb-4 border-b border-slate-100">
                <Typography as="label" className="text-xs font-semibold text-tertiary-600 uppercase tracking-wider">
                  Primary Expertise
                </Typography>
                <Typography as="p" className="text-sm font-medium text-primary-900">
                  {formatArray(registration.primary_expertise)}
                </Typography>
              </div>
              <div className="flex flex-col gap-1.5 pb-4 border-b border-slate-100">
                <Typography as="label" className="text-xs font-semibold text-tertiary-600 uppercase tracking-wider">
                  Preferred Roles
                </Typography>
                <Typography as="p" className="text-sm font-medium text-primary-900">
                  {formatArray(registration.preferred_roles)}
                </Typography>
              </div>
              <div className="flex flex-col gap-1.5 pb-4 border-b border-slate-100 last:border-0 last:pb-0">
                <Typography as="label" className="text-xs font-semibold text-tertiary-600 uppercase tracking-wider">
                  Technical Stack
                </Typography>
                <Typography as="p" className="text-sm font-medium text-primary-900">
                  {formatArray(registration.technical_stack)}
                </Typography>
              </div>
            </div>
          </div>
        </Card>

        {/* Availability & Commitment */}
        <Card className="w-full shadow-sm border border-slate-200">
          <div className="p-6">
            <div className="flex items-center gap-2 mb-6 pb-4 border-b border-slate-200">
              <Icon icon="lucide:clock" className="h-5 w-5 text-purple-600" />
              <Typography as="h3" className="text-lg font-semibold text-primary-900">
                Availability & Commitment
              </Typography>
            </div>
            <div className="space-y-5">
              <div className="flex flex-col gap-1.5 pb-4 border-b border-slate-100">
                <Typography as="label" className="text-xs font-semibold text-tertiary-600 uppercase tracking-wider">
                  Weekly Availability
                </Typography>
                <Typography as="p" className="text-sm font-medium text-primary-900">
                  {formatValue(registration.weekly_availability)}
                </Typography>
              </div>
              <div className="flex flex-col gap-1.5 pb-4 border-b border-slate-100">
                <Typography as="label" className="text-xs font-semibold text-tertiary-600 uppercase tracking-wider">
                  Timezone
                </Typography>
                <Typography as="p" className="text-sm font-medium text-primary-900">
                  {formatValue(registration.timezone)}
                </Typography>
              </div>
              <div className="flex flex-col gap-1.5 pb-4 border-b border-slate-100">
                <Typography as="label" className="text-xs font-semibold text-tertiary-600 uppercase tracking-wider">
                  Commitment Length
                </Typography>
                <Typography as="p" className="text-sm font-medium text-primary-900">
                  {formatValue(registration.commitment_length)}
                </Typography>
              </div>
              <div className="flex flex-col gap-1.5 pb-4 border-b border-slate-100 last:border-0 last:pb-0">
                <Typography as="label" className="text-xs font-semibold text-tertiary-600 uppercase tracking-wider">
                  Communication Preference
                </Typography>
                <Typography as="p" className="text-sm font-medium text-primary-900">
                  {formatValue(registration.communication_preference)}
                </Typography>
              </div>
            </div>
          </div>
        </Card>

        {/* Event Information */}
        <Card className="w-full shadow-sm border border-slate-200">
          <div className="p-6">
            <div className="flex items-center gap-2 mb-6 pb-4 border-b border-slate-200">
              <Icon icon="lucide:calendar" className="h-5 w-5 text-purple-600" />
              <Typography as="h3" className="text-lg font-semibold text-primary-900">
                Event Information
              </Typography>
            </div>
            <div className="space-y-5">
              {registration.event ? (
                <>
                  <div className="flex flex-col gap-1.5 pb-4 border-b border-slate-100">
                    <Typography as="label" className="text-xs font-semibold text-tertiary-600 uppercase tracking-wider">
                      Event Title
                    </Typography>
                    <Typography as="p" className="text-sm font-medium text-primary-900">
                      {registration.event.title}
                    </Typography>
                  </div>
                  <div className="flex flex-col gap-1.5 pb-4 border-b border-slate-100">
                    <Typography as="label" className="text-xs font-semibold text-tertiary-600 uppercase tracking-wider">
                      Event Type
                    </Typography>
                    <Typography as="p" className="text-sm font-medium text-primary-900">
                      {formatValue(registration.event.event_type)}
                    </Typography>
                  </div>
                  <div className="flex flex-col gap-1.5 pb-4 border-b border-slate-100">
                    <Typography as="label" className="text-xs font-semibold text-tertiary-600 uppercase tracking-wider">
                      Start Date
                    </Typography>
                    <Typography as="p" className="text-sm font-medium text-primary-900">
                      {formatDateOnly(registration.event.start_at)}
                    </Typography>
                  </div>
                  <div className="flex flex-col gap-1.5 pb-4 border-b border-slate-100 last:border-0 last:pb-0">
                    <Typography as="label" className="text-xs font-semibold text-tertiary-600 uppercase tracking-wider">
                      End Date
                    </Typography>
                    <Typography as="p" className="text-sm font-medium text-primary-900">
                      {formatDateOnly(registration.event.end_at)}
                    </Typography>
                  </div>
                  {registration.event.id && (
                    <div className="pt-4">
                      <Button
                        variant="secondary"
                        size="small"
                        onClick={() => navigate(`/events/${registration.event.id}`)}
                        startIcon={<Icon icon="lucide:external-link" className="h-4 w-4" />}
                      >
                        View Event Details
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <Typography as="p" className="text-sm text-tertiary-500 italic">
                  No event information available
                </Typography>
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* Application Details */}
      <Card className="w-full shadow-sm border border-slate-200">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-6 pb-4 border-b border-slate-200">
            <Icon icon="lucide:file-text" className="h-5 w-5 text-purple-600" />
            <Typography as="h3" className="text-lg font-semibold text-primary-900">
              Application Details
            </Typography>
          </div>
          <div className="space-y-5">
            <div className="flex flex-col gap-1.5 pb-4 border-b border-slate-100">
              <Typography as="label" className="text-xs font-semibold text-tertiary-600 uppercase tracking-wider">
                Motivation Statement
              </Typography>
              <Typography as="p" className="text-sm font-medium text-primary-900 whitespace-pre-wrap">
                {formatValue(registration.motivation_statement)}
              </Typography>
            </div>
            <div className="flex flex-col gap-1.5 pb-4 border-b border-slate-100">
              <Typography as="label" className="text-xs font-semibold text-tertiary-600 uppercase tracking-wider">
                Previous Volunteering Experience
              </Typography>
              <Typography as="p" className="text-sm font-medium text-primary-900 whitespace-pre-wrap">
                {formatValue(registration.previous_volunteering)}
              </Typography>
            </div>
            <div className="flex flex-col gap-1.5 pb-4 border-b border-slate-100">
              <Typography as="label" className="text-xs font-semibold text-tertiary-600 uppercase tracking-wider">
                Resume URL
              </Typography>
              <Typography as="p" className="text-sm font-medium text-primary-900">
                {registration.resume_url && registration.resume_url !== "-" ? (
                  <a
                    href={registration.resume_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-blue hover:underline flex items-center gap-1"
                  >
                    {registration.resume_url}
                    <Icon icon="lucide:external-link" className="h-3 w-3" />
                  </a>
                ) : (
                  <span className="text-tertiary-500 italic">-</span>
                )}
              </Typography>
            </div>
            <div className="flex flex-col gap-1.5 pb-4 border-b border-slate-100 last:border-0 last:pb-0">
              <Typography as="label" className="text-xs font-semibold text-tertiary-600 uppercase tracking-wider">
                Code of Conduct Accepted
              </Typography>
              <Typography as="p" className="text-sm font-medium text-primary-900">
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                    registration.code_of_conduct_accepted
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {formatValue(registration.code_of_conduct_accepted)}
                </span>
              </Typography>
            </div>
          </div>
        </div>
      </Card>

      {/* Status & Admin Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="w-full shadow-sm border border-slate-200">
          <div className="p-6">
            <div className="flex items-center gap-2 mb-6 pb-4 border-b border-slate-200">
              <Icon icon="lucide:clipboard-check" className="h-5 w-5 text-purple-600" />
              <Typography as="h3" className="text-lg font-semibold text-primary-900">
                Status Information
              </Typography>
            </div>
            <div className="space-y-5">
              <div className="flex flex-col gap-1.5 pb-4 border-b border-slate-100">
                <Typography as="label" className="text-xs font-semibold text-tertiary-600 uppercase tracking-wider">
                  Status
                </Typography>
                <Typography as="p" className="text-sm font-medium text-primary-900">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusColor(registration.status)}`}>
                    {formatStatus(registration.status)}
                  </span>
                </Typography>
              </div>
              <div className="flex flex-col gap-1.5 pb-4 border-b border-slate-100">
                <Typography as="label" className="text-xs font-semibold text-tertiary-600 uppercase tracking-wider">
                  Total Hours Contributed
                </Typography>
                <Typography as="p" className="text-sm font-medium text-primary-900">
                  {formatValue(registration.total_hours_contributed)}
                </Typography>
              </div>
              <div className="flex flex-col gap-1.5 pb-4 border-b border-slate-100 last:border-0 last:pb-0">
                <Typography as="label" className="text-xs font-semibold text-tertiary-600 uppercase tracking-wider">
                  Certificate Issued
                </Typography>
                <Typography as="p" className="text-sm font-medium text-primary-900">
                  <span
                    className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                      registration.certificate_issued
                        ? "bg-green-100 text-green-700"
                        : "bg-slate-100 text-slate-700"
                    }`}
                  >
                    {formatValue(registration.certificate_issued)}
                  </span>
                </Typography>
              </div>
            </div>
          </div>
        </Card>

        <Card className="w-full shadow-sm border border-slate-200">
          <div className="p-6">
            <div className="flex items-center gap-2 mb-6 pb-4 border-b border-slate-200">
              <Icon icon="lucide:settings" className="h-5 w-5 text-purple-600" />
              <Typography as="h3" className="text-lg font-semibold text-primary-900">
                Admin & System Information
              </Typography>
            </div>
            <div className="space-y-5">
              <div className="flex flex-col gap-1.5 pb-4 border-b border-slate-100">
                <Typography as="label" className="text-xs font-semibold text-tertiary-600 uppercase tracking-wider">
                  Admin Review Notes
                </Typography>
                <Typography as="p" className="text-sm font-medium text-primary-900 whitespace-pre-wrap">
                  {formatValue(registration.admin_review_notes)}
                </Typography>
              </div>
              <div className="flex flex-col gap-1.5 pb-4 border-b border-slate-100">
                <Typography as="label" className="text-xs font-semibold text-tertiary-600 uppercase tracking-wider">
                  Registration ID
                </Typography>
                <Typography as="p" className="text-sm font-medium text-primary-900">
                  {formatValue(registration.id)}
                </Typography>
              </div>
              <div className="flex flex-col gap-1.5 pb-4 border-b border-slate-100">
                <Typography as="label" className="text-xs font-semibold text-tertiary-600 uppercase tracking-wider">
                  Created At
                </Typography>
                <Typography as="p" className="text-sm font-medium text-primary-900">
                  {formatDate(registration.created_at)}
                </Typography>
              </div>
              <div className="flex flex-col gap-1.5 pb-4 border-b border-slate-100 last:border-0 last:pb-0">
                <Typography as="label" className="text-xs font-semibold text-tertiary-600 uppercase tracking-wider">
                  Updated At
                </Typography>
                <Typography as="p" className="text-sm font-medium text-primary-900">
                  {formatDate(registration.updated_at)}
                </Typography>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default EventRegistrationDetail;

